/**
 * Fixed JSON bodies for Code Functions HTTP status mapping.
 *
 * Shared by the Blaxel preview runtime and the published Cloudflare worker.
 * This module must stay Zod-free: the worker template inlines this file and
 * must not pull the data-types barrel (or Zod) into the isolate.
 */

/** HTTP methods a declared Function may accept (gateway OPTIONS is separate). */
export const FUNCTIONS_SUPPORTED_METHODS = [
  'GET',
  'POST',
  'PUT',
  'DELETE',
  'PATCH',
] as const;

export type FunctionsSupportedMethod =
  (typeof FUNCTIONS_SUPPORTED_METHODS)[number];

/**
 * Filename-stem ids the CLI accepts. The published worker interpolates the id
 * into a Hono path, so this set excludes pattern chars (`:`, `*`) and
 * URL-significant chars (`?`, `#`, `%`). Keep in lockstep with
 * `VALID_FUNCTION_NAME` in webflow-cli discovery.
 */
export const FUNCTIONS_FUNCTION_ID_PATTERN = /^[\p{L}\p{N}_-]+$/u;

/**
 * Reserved platform-owned function id prefix.
 * Matching is case-insensitive so a `__WF_` stem cannot register as an
 * author function (billing and observability key off this prefix).
 */
export const RESERVED_FUNCTION_ID_PREFIX = '__wf_';

export function isReservedFunctionId(id: string): boolean {
  return id.toLowerCase().startsWith(RESERVED_FUNCTION_ID_PREFIX);
}

/** Unknown module and unknown function share one fixed JSON body. */
export const FUNCTIONS_FUNCTION_NOT_FOUND_ENVELOPE = {
  error: {message: 'Not found', code: 'function_not_found'},
} as const;

/** Known function, unsupported HTTP method. */
export const FUNCTIONS_METHOD_NOT_ALLOWED_ENVELOPE = {
  error: {message: 'Method not allowed', code: 'method_not_allowed'},
} as const;

/** Thrown FunctionParamsError maps to this fixed body; never the throw message. */
export const FUNCTIONS_INVALID_PARAMS_ENVELOPE = {
  error: {message: 'Invalid params', code: 'invalid_params'},
} as const;

/** Any other thrown error maps to this fixed body. */
export const FUNCTIONS_FUNCTION_FAILED_ENVELOPE = {
  error: {message: 'Function execution failed', code: 'function_failed'},
} as const;

/** Combined AbortSignal (timeout + incoming) fired before the module returned. */
export const FUNCTIONS_TIMEOUT_ENVELOPE = {
  error: {message: 'Request timeout', code: 'timeout'},
} as const;

/** Declared Content-Length or consumed request bytes exceeded FUNCTIONS_REQUEST_BODY_MAX_BYTES. */
export const FUNCTIONS_BODY_TOO_LARGE_ENVELOPE = {
  error: {message: 'Request too large', code: 'body_too_large'},
} as const;

/** Over-limit and limiter throw share this body; never the throw message. */
export const FUNCTIONS_RATE_LIMITED_ENVELOPE = {
  error: {message: 'Too many requests', code: 'rate_limited'},
} as const;

export const FUNCTIONS_REQUEST_BODY_MAX_BYTES = 1 * 1024 * 1024;

/**
 * Status mapping for the shared envelopes. Worker and preview must not invent
 * a different code→status pairing for these bodies.
 */
export const FUNCTIONS_ERROR_STATUS_BY_CODE = {
  function_not_found: 404,
  method_not_allowed: 405,
  invalid_params: 400,
  function_failed: 500,
  timeout: 408,
  body_too_large: 413,
  rate_limited: 429,
} as const;

export type FunctionsErrorCode = keyof typeof FUNCTIONS_ERROR_STATUS_BY_CODE;

/**
 * The header name for the Functions preview token.
 */
export const FUNCTIONS_PREVIEW_TOKEN_HEADER = 'x-blaxel-preview-token';

/**
 * The header the Blaxel gateway tags its own refusals with. The Functions
 * runtime never sets it, so its presence tells a gateway rejection apart from
 * one of ours.
 */
export const FUNCTIONS_PREVIEW_ERROR_HEADER = 'x-blaxel-error-code';

/**
 * The `baseUrl` the Designer hands to `useFunction`, which appends the
 * function id. Must stay in step with `matchRawFunctionRoute` in
 * runtime-router-source.ts (Nest) and the baked copy in
 * templates/sandbox/functions/runtime/router.ts, which match
 * /modules/:moduleId/function/:functionId.
 */
export const buildFunctionsPreviewBaseUrl = (
  previewOrigin: string,
  moduleId: string
) => `${previewOrigin}/modules/${encodeURIComponent(moduleId)}/function/`;

/**
 * The headers to send with a Functions preview request.
 */
export const buildFunctionsPreviewHeaders = (token: string) => ({
  [FUNCTIONS_PREVIEW_TOKEN_HEADER]: token,
});

/**
 * The key to use for the Functions sandbox connection.
 * `siteFunctionsHash` is Designer-local: same module IDs plus a new hash
 * produce a new key so preview POSTs again. The server omits this argument.
 */
export const buildFunctionsPreviewKey = (
  moduleIds: readonly string[],
  siteFunctionsHash?: string
): string => {
  const base = [...moduleIds].sort().join(',');
  return siteFunctionsHash ? `${base}:site-hash=${siteFunctionsHash}` : base;
};

/**
 * Mirrors `FunctionsReadiness` in `@webflow/data-types`: a component that sees
 * `pending` and no base URL waits, `settled` means asking again will not help.
 * The package reads an absent value as `settled`, so only widen this union
 * together with the published package.
 */
export type FunctionsReadiness = 'pending' | 'settled';

/** What `buildFunctionsPreviewBaseUrl` and `buildFunctionsPreviewHeaders` consume. */
export type FunctionsPreviewCredentials = {origin: string; token: string};

/**
 * What the Designer hands a Code Island's `functions` config: the credentials
 * when they are usable, whether waiting can still produce them, and the site
 * module id when this preview has selected site Functions.
 *
 * Credentials and readiness travel together on purpose. Split across separate
 * channels, a component could observe a readiness that does not match the
 * credentials it can see.
 *
 * `siteModuleId` is host confirmation, not a second credential. SITE islands
 * propose siteId; they only route when this matches.
 */
export type FunctionsPreviewConfig = {
  credentials: FunctionsPreviewCredentials | null;
  readiness: FunctionsReadiness;
  siteModuleId: string | null;
};
